import React, {
    createContext,
    useContext,
    useState,
    ReactNode,
    useCallback,
  } from 'react';
  import { Snackbar, Alert, AlertColor } from '@mui/material';
  
  // Define the context type
  interface SnackbarContextType {
    showSnackbar: (message: string, severity?: AlertColor) => void;
  }
  
  // Create the context
  const SnackbarContext = createContext<SnackbarContextType | undefined>(undefined);
  
  // Custom hook to use the snackbar
  export const useSnackbar = (): SnackbarContextType => {
    const context = useContext(SnackbarContext);
    if (!context) {
      throw new Error('useSnackbar must be used within a SnackbarProvider');
    }
    return context;
  };
  
  // Define props for the provider
  interface SnackbarProviderProps {
    children: ReactNode;
  }
  
  // Provider component
  export const SnackbarProvider: React.FC<SnackbarProviderProps> = ({ children }) => {
    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState('');
    const [snackbarSeverity, setSnackbarSeverity] = useState<AlertColor>('info');
  
    const showSnackbar = useCallback((message: string, severity: AlertColor = 'info') => {
      setSnackbarMessage(message);
      setSnackbarSeverity(severity);
      setSnackbarOpen(true);
    }, []);
  
    const handleClose = () => setSnackbarOpen(false);
  
    return (
      <SnackbarContext.Provider value={{ showSnackbar }}>
        {children}
        <Snackbar
          open={snackbarOpen}
          autoHideDuration={10000}
          onClose={handleClose}
          sx={{maxWidth: '40vw'}}
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        >
          <Alert
            onClose={handleClose}
            severity={snackbarSeverity}
            sx={{ maxWidth: '40vw' }}
          >
            {snackbarMessage}
          </Alert>
        </Snackbar>
      </SnackbarContext.Provider>
    );
  };
  